import React, { useMemo, useState, useCallback } from 'react';
import { useReactTable, getCoreRowModel, flexRender, ColumnDef } from '@tanstack/react-table';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type { Bond } from '../../types/bonds';
import { BOND_CONSTANTS } from '../../constants/bonds';
import { createBondsTableColumns, TABLE_CONFIG } from '../../config/tableConfig';
import { generateTempId } from '../../utils/idUtils';

interface BondsDataTableProps {
  bonds: Bond[];
  selectedType: string;
  periodsForSelectedTab: string[];
  onBondsChange?: (bonds: Bond[]) => void;
  onSave?: (bonds: Bond[]) => void;
  isSaving?: boolean;
}

interface EditableCellProps {
  value: unknown;
  onSave: (value: unknown) => void;
  displayValue?: string; // Dodajemy displayValue dla lepszego formatowania
  isDate?: boolean; // Dodajemy flagę dla kolumn z datami
}

interface DatePickerCellProps {
  value: string;
  onSave: (value: string) => void;
  displayValue?: string;
}

function DatePickerCell({ value, onSave, displayValue }: DatePickerCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ? new Date(value) : null);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleSave = () => {
    if (selectedDate) {
      const dateString = selectedDate.toISOString().split('T')[0];
      onSave(dateString);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelectedDate(value ? new Date(value) : null);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="datepicker-container">
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          dateFormat="yyyy-MM-dd"
          autoFocus
          className="datepicker-input"
          placeholderText="Wybierz datę"
          isClearable={false}
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={15}
        />
      </div>
    );
  }

  return (
    <div onClick={handleClick} className="editable-cell">
      {displayValue || value || 'Wybierz datę'}
    </div>
  );
}

function EditableCell({ value, onSave, displayValue, isDate }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<unknown>(value);

  const handleClick = () => {
    setIsEditing(true);
    // Ustaw wartość do edycji - usuń formatowanie jeśli istnieje
    if (typeof value === 'string' && value.includes('%')) {
      setEditValue(value.replace('%', ''));
    } else {
      setEditValue(value);
    }
  };

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Jeśli to kolumna z datą, użyj DatePickerCell
  if (isDate) {
    const stringValue = typeof value === 'string' ? value : '';
    return <DatePickerCell value={stringValue} onSave={(value) => onSave(value)} displayValue={displayValue} />;
  }

  if (isEditing) {
    const stringValue = typeof editValue === 'string' ? editValue : '';
    const placeholderValue = typeof value === 'string' ? value : '';
    return (
      <input
        type="text"
        value={stringValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        className="editable-input"
        placeholder={placeholderValue}
      />
    );
  }

  const displayText = displayValue || (typeof value === 'string' ? value : String(value || ''));
  return (
    <div onClick={handleClick} className={`editable-cell ${isEditing ? 'editing' : ''}`}>
      {displayText}
    </div>
  );
}

export default function BondsDataTable({ bonds, selectedType, periodsForSelectedTab, onBondsChange, onSave, isSaving = false }: BondsDataTableProps) {
  // Filtruj obligacje na podstawie selectedType
  const filteredBonds = React.useMemo(() => {
    if (!bonds || !selectedType) return [];
    return bonds.filter((bond) => bond.type === selectedType);
  }, [bonds, selectedType]);

  const [localBonds, setLocalBonds] = useState<Bond[]>(filteredBonds);

  // Update local bonds when filtered bonds change
  React.useEffect(() => {
    setLocalBonds(filteredBonds);
  }, [filteredBonds]);

  const handleCellEdit = useCallback(
    (rowIndex: number, columnId: string, value: unknown) => {
      const updatedFilteredBonds = [...localBonds];
      const bond = updatedFilteredBonds[rowIndex];

      // Dodatkowa weryfikacja - znajdź wiersz po ID jeśli rowIndex nie działa poprawnie
      if (!bond || bond.id === '') {
        console.warn('Nieprawidłowy rowIndex, próbuję znaleźć wiersz po ID');
        return;
      }

      if (columnId.startsWith('period_')) {
        const period = columnId.replace('period_', '');
        // Dla oprocentowania - wartość jest już w procentach, więc dzielimy przez 100
        if (typeof value === 'string' || typeof value === 'number') {
          bond.interestRates[period] = parseFloat(String(value)) / 100;
        }
      } else if (columnId === 'cumulatedValue') {
        if (typeof value === 'string' || typeof value === 'number') {
          bond.interestRates[BOND_CONSTANTS.CUMULATED_VALUE_KEY] = parseFloat(String(value));
        }
      } else if (columnId === 'bondDurationInMonths' || columnId === 'periodDurationInMonths') {
        if (typeof value === 'string' || typeof value === 'number') {
          (bond as unknown as Record<string, unknown>)[columnId] = parseInt(String(value));
        }
      } else if (columnId === 'sellingStartDate' || columnId === 'sellingEndDate') {
        (bond as unknown as Record<string, unknown>)[columnId] = value;
      } else if (columnId === 'type') {
        // Specjalna obsługa zmiany typu obligacji
        const oldType = bond.type;
        if (typeof value === 'string') {
          bond.type = value;
        }

        // Jeśli zmieniamy typ obligacji, musimy upewnić się, że nowy typ istnieje
        // i że obligacja pozostanie widoczna w aktualnej zakładce
        const uniqueTypes = [...new Set(updatedFilteredBonds.map((b) => b.type))].sort();
        if (uniqueTypes.length === 0) {
          // Jeśli nie ma żadnych typów, przywróć stary typ
          bond.type = oldType;
          return;
        }
      } else {
        (bond as unknown as Record<string, unknown>)[columnId] = value;
      }

      // Aktualizuj wszystkie obligacje, zastępując te z aktualnego typu
      const updatedAllBonds = bonds.map((b) => {
        if (b.id === bond.id) {
          return bond;
        }
        return b;
      });

      // Jeśli zmieniliśmy typ obligacji, usuń ją z lokalnych obligacji (przefiltrowanych)
      let finalLocalBonds = updatedFilteredBonds;
      if (columnId === 'type' && bond.type !== selectedType) {
        finalLocalBonds = updatedFilteredBonds.filter((_, index) => index !== rowIndex);
      }

      setLocalBonds(finalLocalBonds);
      onBondsChange?.(updatedAllBonds);
    },
    [localBonds, bonds, selectedType, onBondsChange],
  );

  const handleAddRow = useCallback(() => {
    // Użyj typu z aktualnie wybranej zakładki lub domyślnego typu
    const currentType = selectedType || '';

    const lastBond = bonds?.filter((b) => b.type === currentType && b.name !== '').pop();

    const newBond: Bond = {
      id: generateTempId(), // Użyj tymczasowego ID zamiast pustego stringa
      name: lastBond?.name || '',
      type: currentType,
      ticker: '',
      sellingStartDate: new Date().toISOString().split('T')[0],
      sellingEndDate: new Date().toISOString().split('T')[0],
      bondDurationInMonths: lastBond?.bondDurationInMonths || 0,
      periodDurationInMonths: lastBond?.periodDurationInMonths || 0,
      interestRates: {},
    };

    // Dodaj nowy wiersz na początku tablicy (na górze tabeli)
    const updatedAllBonds = [newBond, ...bonds];
    const updatedFilteredBonds = [newBond, ...localBonds];
    setLocalBonds(updatedFilteredBonds);
    onBondsChange?.(updatedAllBonds);
  }, [bonds, selectedType, onBondsChange, localBonds]);

  const handleDeleteRow = useCallback((rowIndex: number) => {
    const bondToDelete = localBonds[rowIndex];

    // Dodatkowa weryfikacja
    if (!bondToDelete) {
      console.warn('Nie można znaleźć wiersza do usunięcia');
      return;
    }

    const updatedFilteredBonds = localBonds.filter((_, index) => index !== rowIndex);
    const updatedAllBonds = bonds.filter((b) => b.id !== bondToDelete.id);

    setLocalBonds(updatedFilteredBonds);
    onBondsChange?.(updatedAllBonds);
  }, [localBonds, bonds, onBondsChange]);

  const handleSave = () => {
    // Aktualizuj wszystkie obligacje z lokalnymi zmianami
    const updatedAllBonds = bonds.map((b) => {
      const localBond = localBonds.find((lb) => lb.id === b.id);
      return localBond || b;
    });
    onSave?.(updatedAllBonds);
  };

  const columns = useMemo<ColumnDef<Bond, unknown>[]>(
    () => createBondsTableColumns(periodsForSelectedTab, handleCellEdit, handleDeleteRow, EditableCell),
    [periodsForSelectedTab, handleCellEdit, handleDeleteRow],
  );

  const table = useReactTable({
    data: localBonds,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id, // Użyj pola id jako klucza wiersza
  });

  return (
    <div className="table-container">
      <div className="table-actions">
        <div className="table-hint">{TABLE_CONFIG.hints.editHint}</div>
        <div className="table-buttons">
          <button onClick={handleAddRow} className="add-row-button">
            {TABLE_CONFIG.buttons.addRow}
          </button>
          <button onClick={handleSave} className="save-button" disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="save-spinner"></span>
                Zapisywanie...
              </>
            ) : (
              TABLE_CONFIG.buttons.save
            )}
          </button>
        </div>
      </div>

      {localBonds.length === 0 ? (
        <div className="empty-table-message">
          <p>{TABLE_CONFIG.emptyMessage.title}</p>
          <p>{TABLE_CONFIG.emptyMessage.subtitle}</p>
        </div>
      ) : (
        <table className="bonds-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
